/**
 * useAuth Hook
 *
 * Provides authentication state management and auth operations
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase, createUserProfile } from '../lib/supabase';
import api from '../services/api';

// Profile cache to prevent rate limiting (stored outside component)
const profileCache = {
  data: null,
  userId: null,
  timestamp: 0,
  CACHE_DURATION: 60000, // 60 seconds
};

export function useAuth() {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // Fetch user profile from backend API (bypasses Supabase RLS issues)
  const fetchUserProfile = useCallback(async (userId) => {
    // Check cache first to avoid rate limiting
    const now = Date.now();
    if (
      profileCache.userId === userId &&
      profileCache.data &&
      (now - profileCache.timestamp) < profileCache.CACHE_DURATION
    ) {
      console.log('[useAuth] Using cached profile');
      setUserProfile(profileCache.data);
      return profileCache.data;
    }
    if (!userId) {
      console.log('[useAuth] fetchUserProfile: No userId provided');
      return null;
    }

    console.log('[useAuth] Fetching profile for userId:', userId);

    try {
      // Use backend API to get user profile - this bypasses RLS issues
      const response = await api.get('/auth/me');

      if (response.data?.success && response.data?.data) {
        const userData = response.data.data;

        // Build the profile object
        // For admin users, use role if user_type is not admin
        const effectiveUserType = userData.role === 'admin' ? 'admin' : userData.user_type;
        
        const profile = {
          id: userData.id,
          email: userData.email,
          user_type: effectiveUserType,
          role: userData.role,
          buyer_id: userData.buyer_id,
          seller_id: userData.seller_id,
          is_also_seller: userData.is_also_seller,
          company_modalities: userData.company_modalities,
          subscription_tier: userData.subscription_tier,
          last_login: userData.last_login,
          created_at: userData.created_at,
          buyers: userData.buyer || null,
          sellers: userData.seller || null
        };

        console.log('[useAuth] Profile fetched successfully:', {
          id: profile.id,
          email: profile.email,
          user_type: profile.user_type,
          seller_id: profile.seller_id,
          buyer_id: profile.buyer_id,
          hasSellers: !!profile.sellers,
          hasBuyers: !!profile.buyers
        });

        // Update cache
        profileCache.data = profile;
        profileCache.userId = userId;
        profileCache.timestamp = Date.now();

        setUserProfile(profile);
        return profile;
      }

      // No profile found - return minimal profile for new users
      console.log('[useAuth] No profile in response, returning minimal profile');
      const minimalProfile = {
        id: userId,
        user_type: null,
        buyer_id: null,
        seller_id: null,
        buyers: null,
        sellers: null
      };
      setUserProfile(minimalProfile);
      return minimalProfile;
    } catch (err) {
      // Handle 401 - user not in database yet
      if (err.response?.status === 401) {
        console.log('[useAuth] User not authenticated or not in database yet');
        const minimalProfile = {
          id: userId,
          user_type: null,
          buyer_id: null,
          seller_id: null,
          buyers: null,
          sellers: null
        };
        setUserProfile(minimalProfile);
        return minimalProfile;
      }

      console.error('[useAuth] Profile fetch error:', err.message);
      // Return minimal profile to avoid blocking
      const minimalProfile = {
        id: userId,
        user_type: null,
        buyer_id: null,
        seller_id: null,
        buyers: null,
        sellers: null
      };
      setUserProfile(minimalProfile);
      return minimalProfile;
    }
  }, []);

  // Initialize auth state
  useEffect(() => {
    let mounted = true;
    let initComplete = false;
    let profileFetchInProgress = false;

    const initAuth = async () => {
      console.log('[useAuth] 🔄 Starting auth initialization...');

      // Set a timeout - if auth takes more than 15 seconds, something is wrong
      const timeout = setTimeout(() => {
        if (mounted && !initComplete) {
          console.warn('[useAuth] ⏱️ Auth init timeout after 15s - setting loading to false');
          // Don't sign out - just stop loading to prevent UI from being stuck
          setLoading(false);
          initComplete = true;
        }
      }, 15000);

      try {
        // Get current session
        console.log('[useAuth] 📡 Fetching session from Supabase...');
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();

        if (sessionError) {
          console.error('[useAuth] ❌ Session error:', sessionError);
          if (mounted) {
            setLoading(false);
          }
          clearTimeout(timeout);
          initComplete = true;
          return;
        }

        console.log('[useAuth] 📦 Session fetched:', {
          hasSession: !!session,
          hasUser: !!session?.user,
          userId: session?.user?.id,
          expiresAt: session?.expires_at ? new Date(session.expires_at * 1000).toISOString() : 'N/A'
        });

        if (mounted) {
          if (session?.user) {
            console.log('[useAuth] ✅ User authenticated, setting user state');
            setUser(session.user);
            // Fetch profile - don't use abort controller to avoid race conditions
            if (!profileFetchInProgress) {
              profileFetchInProgress = true;
              try {
                await fetchUserProfile(session.user.id);
              } catch (profileErr) {
                console.error('[useAuth] Profile fetch failed:', profileErr);
              } finally {
                profileFetchInProgress = false;
              }
            }
          } else {
            console.warn('[useAuth] ⚠️ No session found - user will need to login');
          }
          console.log('[useAuth] 🏁 Auth init complete, setting loading = false');
          setLoading(false);
        }
        clearTimeout(timeout);
        initComplete = true;
      } catch (err) {
        console.error('[useAuth] Auth init error:', err);
        clearTimeout(timeout);
        initComplete = true;
        if (mounted) setLoading(false);
      }
    };

    initAuth();

    // Listen for auth changes - only handle SIGNED_IN and SIGNED_OUT events
    // Skip INITIAL_SESSION to avoid race with initAuth
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('[useAuth] Auth state changed:', event);

        // Skip INITIAL_SESSION as initAuth handles it
        if (event === 'INITIAL_SESSION') {
          return;
        }

        // Handle token refresh - update user and re-fetch profile
        if (event === 'TOKEN_REFRESHED') {
          console.log('[useAuth] Token refreshed, re-fetching profile');
          if (mounted && session?.user) {
            setUser(session.user);
            // Re-fetch profile to ensure we have latest data after token refresh
            if (!profileFetchInProgress) {
              profileFetchInProgress = true;
              try {
                await fetchUserProfile(session.user.id);
              } catch (profileErr) {
                console.error('[useAuth] Profile fetch after token refresh failed:', profileErr);
              } finally {
                profileFetchInProgress = false;
              }
            }
          }
          return;
        }

        if (mounted) {
          if (session?.user) {
            setUser(session.user);
            // Prevent duplicate profile fetches
            if (!profileFetchInProgress) {
              profileFetchInProgress = true;
              try {
                await fetchUserProfile(session.user.id);
              } catch (profileErr) {
                console.error('[useAuth] Profile fetch failed:', profileErr);
              } finally {
                profileFetchInProgress = false;
              }
            }
          } else if (event === 'SIGNED_OUT') {
            // Only clear user on explicit sign out
            console.log('[useAuth] SIGNED_OUT event received, clearing user state');
            setUser(null);
            setUserProfile(null);
          } else {
            // For any other event without session, log but don't clear user
            // This prevents false logouts from token refresh or network issues
            console.warn('[useAuth] Auth event without session:', event, '- NOT clearing user (only SIGNED_OUT clears)');
          }
          setLoading(false);
        }
      }
    );

    return () => {
      mounted = false;
      subscription?.unsubscribe();
    };
  }, [fetchUserProfile]);

  // Sign up with email and password
  const signUp = useCallback(async (email, password, userType, companyInfo) => {
    setError(null);
    try {
      const { data, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            user_type: userType,
            company_name: companyInfo?.company?.company_name,
          },
          emailRedirectTo: `${window.location.origin}/auth/callback`
        }
      });

      if (signUpError) throw signUpError;

      return { data, error: null };
    } catch (err) {
      setError(err.message);
      return { data: null, error: err };
    }
  }, []);

  // Verify OTP code
  const verifyOtp = useCallback(async (email, token, userType, companyInfo) => {
    setError(null);
    try {
      const { data, error: verifyError } = await supabase.auth.verifyOtp({
        email,
        token,
        type: 'email'
      });

      if (verifyError) throw verifyError;

      // Create/update user profile after verification
      if (data.user) {
        const { error: profileError } = await createUserProfile(
          data.user,
          userType,
          companyInfo
        );

        if (profileError) {
          console.error('Profile creation error:', profileError);
        }

        // Fetch the profile
        await fetchUserProfile(data.user.id);
      }

      return { data, error: null };
    } catch (err) {
      setError(err.message);
      return { data: null, error: err };
    }
  }, [fetchUserProfile]);

  // Sign in with email and password
  const signIn = useCallback(async (email, password) => {
    setError(null);
    try {
      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (signInError) throw signInError;

      // Save email for next login
      localStorage.setItem('pharmunity_last_email', email);

      // Update last_login
      if (data.user) {
        await supabase
          .from('users')
          .update({ last_login: new Date().toISOString() })
          .eq('id', data.user.id);

        // Fetch profile
        const profile = await fetchUserProfile(data.user.id);

        // Redirect based on user type
        const isAdmin = profile?.role === 'admin' || profile?.user_type === 'admin';
        if (isAdmin) {
          navigate('/admin/dashboard');
        } else if (profile?.user_type === 'buyer') {
          navigate('/buyer/dashboard');
        } else {
          navigate('/seller/dashboard');
        }
      }

      return { data, error: null };
    } catch (err) {
      setError(err.message);
      return { data: null, error: err };
    }
  }, [navigate, fetchUserProfile]);

  // Sign out
  const signOut = useCallback(async () => {
    try {
      await supabase.auth.signOut();
      // Clear profile cache
      profileCache.data = null;
      profileCache.userId = null;
      profileCache.timestamp = 0;
      setUser(null);
      setUserProfile(null);

      // Clear localStorage session tokens
      localStorage.removeItem('pharmunity-auth-token');
      localStorage.removeItem('sb-abbccnfcbbxzpxwvjjow-auth-token');

      // Force page reload to clear all in-memory caches (including api.js sessionCache)
      window.location.href = '/login';
    } catch (err) {
      console.error('Sign out error:', err);
      // Even on error, try to redirect
      window.location.href = '/login';
    }
  }, []);

  // Request password reset
  const resetPassword = useCallback(async (email) => {
    setError(null);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/reset-password`
      });

      if (error) throw error;
      return { error: null };
    } catch (err) {
      setError(err.message);
      return { error: err };
    }
  }, []);

  // Update password (after reset)
  const updatePassword = useCallback(async (newPassword) => {
    setError(null);
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) throw error;
      return { error: null };
    } catch (err) {
      setError(err.message);
      return { error: err };
    }
  }, []);

  // Resend verification email
  const resendVerification = useCallback(async (email) => {
    setError(null);
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email
      });

      if (error) throw error;
      return { error: null };
    } catch (err) {
      setError(err.message);
      return { error: err };
    }
  }, []);

  // Create a unified user object with camelCase field names for components
  const unifiedUser = user && userProfile ? {
    // Auth user fields
    id: user.id,
    email: user.email,
    // Profile fields with camelCase aliases
    userType: userProfile.user_type,
    sellerId: userProfile.seller_id,
    buyerId: userProfile.buyer_id,
    isAlsoSeller: userProfile.is_also_seller,
    companyModalities: userProfile.company_modalities,
    subscriptionTier: userProfile.subscription_tier,
    // Related data
    buyer: userProfile.buyers,
    seller: userProfile.sellers,
    // Original profile for backwards compatibility
    ...userProfile
  } : null;

  // Debug logging for auth state
  if (process.env.NODE_ENV === 'development' && !loading) {
    console.log('[useAuth] State:', {
      hasAuthUser: !!user,
      hasUserProfile: !!userProfile,
      hasUnifiedUser: !!unifiedUser,
      sellerId: userProfile?.seller_id,
      userType: userProfile?.user_type
    });
  }

  return {
    user: unifiedUser,
    userProfile,
    authUser: user, // Original Supabase auth user if needed
    loading,
    error,
    isAuthenticated: !!user,
    isAdmin: userProfile?.role === 'admin' || userProfile?.user_type === 'admin',
    isBuyer: userProfile?.user_type === 'buyer',
    isSeller: userProfile?.user_type === 'seller',
    signUp,
    signIn,
    login: signIn, // Alias for backwards compatibility
    signOut,
    logout: signOut, // Alias for backwards compatibility
    verifyOtp,
    resetPassword,
    updatePassword,
    resendVerification,
    refreshProfile: () => user?.id ? fetchUserProfile(user.id) : Promise.resolve(null)
  };
}

export default useAuth;
